import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_da037825-faa5-420e-a579-84f9393da584/artifacts/qxg7ev86_redeemlogo.jpeg";

const navLinks = [
  { label: "Home", target: "home" },
  { label: "About", target: "about" },
  { label: "Services", target: "services" },
  { label: "Events", target: "events" },
  { label: "Gallery", target: "gallery" },
  { label: "Contact", target: "contact" },
  { label: "Prayer", target: "prayer" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = useCallback((targetId) => {
    // Close menu first, then scroll after a short delay so the menu exit doesn't block scrolling
    setMobileOpen(false);
    setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el) {
        const headerOffset = 70;
        const elementPosition = el.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - headerOffset, behavior: "smooth" });
      }
    }, 150);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white backdrop-blur-xl shadow-lg border-b border-slate-100"
          : "bg-white/90 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        {/* Logo */}
        <button onClick={() => scrollTo("home")} className="flex items-center gap-3 cursor-pointer" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="RCCG Logo"
            className="w-11 h-11 rounded-full object-cover shadow-md"
          />
          <div className="leading-tight text-left">
            <span className="font-heading font-bold text-crimson text-lg block">
              Dominion Parish
            </span>
            <span className="text-xs text-slate-600 font-body tracking-wide">
              RCCG Felixstowe
            </span>
          </div>
        </button>

        {/* Desktop nav - dark navy text for contrast */}
        <nav className="hidden md:flex items-center gap-2" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <button
              key={link.target}
              onClick={() => scrollTo(link.target)}
              className="px-4 py-2 rounded-full text-sm font-body font-semibold text-navy hover:text-crimson hover:bg-crimson/5 transition-all duration-300 cursor-pointer"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 rounded-full hover:bg-navy/5 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-b border-slate-100"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-4 gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.target}
                  onClick={() => scrollTo(link.target)}
                  className="px-4 py-3 rounded-2xl text-navy font-body font-semibold hover:bg-navy/5 transition-all text-left active:bg-navy/10"
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
